import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Copy, MapPin, Share2 } from 'lucide-react';
import api from '../lib/api';
import CountyMap from '../components/CountyMap';

export default function SharedItinerary() {
  const { token } = useParams();
  const [it, setIt] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get(`/itineraries/${token}`).then((r) => setIt(r.data)).catch(() => setIt({ notFound: true }));
    window.scrollTo(0, 0);
  }, [token]);

  function share() {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  if (!it) return <div className="py-32 text-center text-muted">Loading...</div>;
  if (it.notFound) return (
    <div className="max-w-[1100px] mx-auto px-6 py-32 text-center">
      <h1 className="font-display text-4xl">Itinerary not found</h1>
      <Link to="/plan" className="text-heritage mt-4 inline-block">← Build a new one</Link>
    </div>
  );

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16">
      <Link to="/plan" className="inline-flex items-center gap-2 text-muted hover:text-heritage mb-6"><ArrowLeft size={16}/> Build your own</Link>
      <div className="flex items-start justify-between flex-wrap gap-4 mb-12">
        <div>
          <div className="label text-heritage mb-3">Shared itinerary</div>
          <h1 className="font-display text-5xl lg:text-7xl leading-[0.95]">{it.title}</h1>
          <div className="mt-3 text-muted">{it.attractions?.length || 0} stops in Leicestershire</div>
        </div>
        <button onClick={share} data-testid="shared-itin-copy" className="bg-charcoal text-base px-5 py-3 rounded-full font-semibold inline-flex items-center gap-2 hover:bg-charcoal/90">
          {copied ? <><Copy size={14}/> Copied!</> : <><Share2 size={14}/> Share</>}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
        <div className="lg:col-span-7 space-y-4">
          {it.attractions?.map((a, i) => (
            <Link to={`/explore/${a.slug}`} key={a.id} data-testid={`shared-stop-${a.slug}`} className="group bg-white border border-charcoal/10 rounded-3xl overflow-hidden grid grid-cols-1 md:grid-cols-3 gap-0 hover:shadow-soft transition-all">
              <div className="md:col-span-1 aspect-[4/3] overflow-hidden relative">
                <img src={a.hero_image} alt={a.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
                <div className="absolute top-3 left-3 w-9 h-9 bg-heritage text-base rounded-full flex items-center justify-center font-display text-lg shadow-soft">{i + 1}</div>
              </div>
              <div className="md:col-span-2 p-6 flex flex-col justify-center">
                <h3 className="font-display text-2xl mb-2 group-hover:text-heritage">{a.name}</h3>
                <div className="text-muted mb-2 flex items-center gap-1.5"><MapPin size={14}/> {a.town}</div>
                <p className="text-charcoal/85 text-sm line-clamp-3">{a.summary || a.description?.slice(0, 200)}</p>
              </div>
            </Link>
          ))}
        </div>
        <div className="lg:col-span-5">
          <div className="sticky top-28">
            <CountyMap items={it.attractions || []} height={520}/>
          </div>
        </div>
      </div>

      <p
        data-testid="shared-itin-disclaimer"
        className="text-xs italic text-charcoal/55 leading-relaxed max-w-3xl mt-2 mb-4"
      >
        Times, opening hours and access details can change — please double-check with each venue before you set off. &quot;Free&quot; attractions may still charge for parking. Leave a little wiggle room for lunch, the weather and a proper cup of tea.
      </p>
    </div>
  );
}
